<script setup>
import { ref, reactive, computed, onMounted, inject, nextTick, watchEffect } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM'
import { ENTREPOSTO_ST_REGIME } from '@/constants/ENTREPOSTO_ST_REGIME'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idEntrepostoAduaneiro: 0,
      idOrganizacao: null,
      idFilial: null,
      numeroDocumentoAdmissao: '',
      dataRegistroAdmissao: new Date().toISOString().split('T')[0],
      lstStatusRegime: ENTREPOSTO_ST_REGIME.Ativo,
      idLocalEstoque: null,
      dataConcessao: null,
      dataVencimentoOriginal: null,
      isRegimeProrrogado: false,
      dataVencimentoProrrogada: null,
      gerarTransferenciaEstoque: true
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

const isLoadingListas = ref(false)

const accordionState = reactive({
  identificacao: true,
  temporalidade: false,
  itens: true
})
const listas = reactive({})

const formErrors = inject('formErrors', ref({}))

const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode && authStore.pode('ENTREPOSTO_COMPLETO')) return true
  const perms = authStore.getPermsDoModulo && authStore.getPermsDoModulo('ENTREPOSTO')
  return perms && perms.some(p => p === 'COMPLETO' || p.includes('COMPLETO'))
})
const isReadOnly = computed(() => isEditing.value && !temPermissaoCompleta.value)

// Status original do registro (capturado ao entrar em edição) para bloqueio de campos
const statusOriginal = ref(null)

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

// ============================================================
// LÓGICA "SALVAR CABEÇALHO PRIMEIRO, DEPOIS GERENCIAR ITENS"
// ============================================================
// O regime precisa ser salvo (POST) antes de permitir adicionar PLPs.
// Os itens são gerenciados via endpoints dedicados (EntrepostoAduaneiroItemsController).
const isRegimeSaved = ref(isEditing.value)
const isSavingRegimeLocal = ref(false)
const isItensFrameBlocked = computed(() => !isRegimeSaved.value || isReadOnly.value)

const regimeSalvo = computed(() => {
  return item.value.idEntrepostoAduaneiro > 0
})

// ============================================================
// MODO DE TRANSFERÊNCIA (Modo A vs Modo B)
// ============================================================
const gerarTransferencia = computed({
  get: () => item.value.gerarTransferenciaEstoque !== false,
  set: (val) => { item.value.gerarTransferenciaEstoque = val }
})

const modoLabel = computed(() => {
  return gerarTransferencia.value
    ? 'Modo A — Filial/Local atuam como DESTINO (gerará transferência de estoque)'
    : 'Modo B — Filial/Local atuam como FILTRO (apenas vinculação declarativa)'
})

const camposTemporalidadeLocked = computed(() => {
  if (!isEditing.value) return false
  return statusOriginal.value && statusOriginal.value !== ENTREPOSTO_ST_REGIME.Ativo
})

const camposIdentificacaoLocked = computed(() => {
  if (!isEditing.value) return false
  return statusOriginal.value && statusOriginal.value !== ENTREPOSTO_ST_REGIME.Ativo
})

const entityInfoCalculado = computed(() => {
  if (!isEditing.value) return entityInfo.value
  return {
    id: entityInfo.value?.id || item.value.idEntrepostoAduaneiro,
    name: item.value.numeroDocumentoAdmissao || 'Novo Regime',
    nameLabel: 'Entreposto Aduaneiro'
  }
})

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaLocalEstoque = computed(() =>
  criarConfigBuscaLocalEstoque(authStore.organizacaoAtiva, item.value.idFilial, [ESTQ_TIPO_LOCAL_ESTOCAGEM.AlfandegadoOuEntreposto])
)

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['EntrepostoAduaneiro'])
    Object.assign(listas, result)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados auxiliares.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

// ============================================================
// CÁLCULO DE VENCIMENTO
// ============================================================
watchEffect(() => {
  if (item.value.dataConcessao && !isEditing.value) {
    const concessao = new Date(item.value.dataConcessao)
    concessao.setFullYear(concessao.getFullYear() + 1)
    item.value.dataVencimentoOriginal = concessao.toISOString().split('T')[0]
  }
})

// ============================================================
// VALIDAÇÃO DO CABEÇALHO
// ============================================================
const validarFormulario = () => {
  const erros = {}
  const camposComErro = []

  formErrors.value = {}

  // Campos obrigatórios sempre
  if (!item.value.idFilial) {
    erros.idFilial = 'Filial é obrigatória.'
    camposComErro.push('idFilial')
  }
  if (!item.value.numeroDocumentoAdmissao) {
    erros.numeroDocumentoAdmissao = 'Nº do Documento de Admissão é obrigatório.'
    camposComErro.push('numeroDocumentoAdmissao')
  }
  if (!item.value.dataRegistroAdmissao) {
    erros.dataRegistroAdmissao = 'Data de Registro é obrigatória.'
    camposComErro.push('dataRegistroAdmissao')
  }
  if (!item.value.idLocalEstoque) {
    erros.idLocalEstoque = gerarTransferencia.value
      ? 'Local de destino é obrigatório para gerar transferência'
      : 'Selecione o recinto alfandegado onde o material já está'
    camposComErro.push('idLocalEstoque')
  }
  if (!item.value.dataConcessao) {
    erros.dataConcessao = 'Data de Concessão é obrigatória.'
    camposComErro.push('dataConcessao')
  }
  if (!item.value.dataVencimentoOriginal) {
    erros.dataVencimentoOriginal = 'Data de Vencimento é obrigatória.'
    camposComErro.push('dataVencimentoOriginal')
  }

  // Validações por status
  if (item.value.lstStatusRegime === ENTREPOSTO_ST_REGIME.Prorrogado) {
    if (!item.value.dataVencimentoProrrogada) {
      erros.dataVencimentoProrrogada = 'Data de Vencimento Prorrogada é obrigatória para regime prorrogado.'
      camposComErro.push('dataVencimentoProrrogada')
    }
  }

  // Prorrogação só permitida se ainda houver dias para vencimento
  if (item.value.isRegimeProrrogado && item.value.dataVencimentoOriginal) {
    const hoje = new Date()
    const vencimento = new Date(item.value.dataVencimentoOriginal)
    const dias = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24))
    if (dias <= 0) {
      erros.isRegimeProrrogado = 'Não é possível prorrogar um regime já vencido.'
      camposComErro.push('isRegimeProrrogado')
    }
  }

  formErrors.value = erros

  if (camposComErro.length > 0) {
    const mensagens = camposComErro.map(c => erros[c]).join('; ')
    emit('show-notification', { message: `Preencha os campos obrigatórios: ${mensagens}`, type: 'warning' })
    nextTick(() => {
      const el = document.getElementById(camposComErro[0])
      if (el) {
        el.focus()
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
    return false
  }

  return true
}

// ============================================================
// SALVAR CABEÇALHO (POST intermediário — libera o frame de itens)
// ============================================================
const salvarRegimeLocalmente = async () => {
  if (isReadOnly.value) return
  if (!validarFormulario()) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  isSavingRegimeLocal.value = true

  const payload = {
    ...item.value,
    gerarTransferenciaEstoque: item.value.gerarTransferenciaEstoque !== false
  }

  try {
    const response = await api.post('/EntrepostoAduaneiros', payload)
    item.value = response.data

    isRegimeSaved.value = true
    isEditing.value = true
    statusOriginal.value = item.value.lstStatusRegime

    emit('show-notification', { message: 'Regime salvo. Agora pode adicionar os PLPs.', type: 'success' })

    accordionState.itens = true
    await carregarItensRegime()
  } catch (error) {
    const message = error?.response?.data?.message || 'Erro ao salvar o regime.'
    emit('show-notification', { message, type: 'error' })
  } finally {
    isSavingRegimeLocal.value = false
  }
}

// ============================================================
// SALVAR PRINCIPAL (topo — atualiza cabeçalho)
// ============================================================
const salvar = async () => {
  if (isReadOnly.value) return
  if (!validarFormulario()) return

  const payload = {
    ...item.value
  }

  try {
    await submitForm('EntrepostoAduaneiro', 'idEntrepostoAduaneiro', payload)
  } catch (error) {
    const message = error?.response?.data?.message || 'Erro ao salvar regime de entreposto.'
    emit('show-notification', { message, type: 'error' })
  }
}

// ============================================================
// ITENS DO REGIME (endpoints dedicados)
// ============================================================
const itensRegime = ref([])
const isLoadingItens = ref(false)

const carregarItensRegime = async () => {
  if (!item.value.idEntrepostoAduaneiro) {
    itensRegime.value = []
    return
  }

  isLoadingItens.value = true
  try {
    const res = await api.get(`/EntrepostoAduaneiroItems/por-regime/${item.value.idEntrepostoAduaneiro}`)
    itensRegime.value = res.data || []
  } catch (e) {
    console.error('Erro ao carregar itens do regime:', e)
    itensRegime.value = []
  } finally {
    isLoadingItens.value = false
  }
}

// ============================================================
// SMARTMODE - PLPs Disponíveis (com paginação)
// ============================================================
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const mostrarPainelPLPs = ref(false)
const plpsDisponiveis = ref([])
const isLoadingPLPs = ref(false)
const filtroEmbarque = ref('')
const filtroProduto = ref('')
const embarquesAgrupados = ref([])
const embarqueExpandido = ref(null)

const carregarPLPsDisponiveis = async (pageNumber = null) => {
  isLoadingPLPs.value = true
  try {
    const params = injetarParametros(pageNumber || paginacao.value.CurrentPage)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (item.value.idFilial) params.append('idFilial', item.value.idFilial)
    if (item.value.idLocalEstoque) params.append('idLocalEstoque', item.value.idLocalEstoque)
    params.append('gerarTransferencia', item.value.gerarTransferenciaEstoque !== false ? 'true' : 'false')
    if (filtroEmbarque.value) params.append('idEmbarque', filtroEmbarque.value)
    if (filtroProduto.value) params.append('termoProduto', filtroProduto.value)

    const res = await api.get(`/EntrepostoAduaneiros/plps-disponiveis?${params.toString()}`)
    plpsDisponiveis.value = res.data || []
    atualizarPaginacao(res.headers)

    // Agrupar por embarque
    const grupos = {}
    for (const plp of plpsDisponiveis.value) {
      const key = plp.idEmbarque
      if (!grupos[key]) {
        grupos[key] = {
          idEmbarque: key,
          numeroEmbarque: plp.numeroEmbarque,
          plps: []
        }
      }
      grupos[key].plps.push(plp)
    }
    embarquesAgrupados.value = Object.values(grupos)
  } catch (e) {
    console.error('Erro ao carregar PLPs disponíveis:', e)
    emit('show-notification', { message: 'Erro ao carregar PLPs disponíveis.', type: 'error' })
    plpsDisponiveis.value = []
    embarquesAgrupados.value = []
  } finally {
    isLoadingPLPs.value = false
  }
}

const mudarPaginaPLPs = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    carregarPLPsDisponiveis(page)
  }
}

const togglePainelPLPs = () => {
  mostrarPainelPLPs.value = !mostrarPainelPLPs.value
  if (mostrarPainelPLPs.value && plpsDisponiveis.value.length === 0) {
    carregarPLPsDisponiveis()
  }
}

const toggleEmbarqueExpandido = (idEmbarque) => {
  embarqueExpandido.value = embarqueExpandido.value === idEmbarque ? null : idEmbarque
}

// ============================================================
// ADICIONAR PLP (POST via endpoint dedicado)
// ============================================================
const isSavingItem = ref(false)

const adicionarPLP = async (plp) => {
  if (!isRegimeSaved.value) return

  // Verificar se já está na lista
  const jaExiste = itensRegime.value.some(
    i => i.idEmbarquePackingListProduto === plp.idEmbarquePackingListProduto
  )
  if (jaExiste) {
    emit('show-notification', { message: 'Este PLP já está alocado no regime.', type: 'warning' })
    return
  }

  isSavingItem.value = true
  try {
    const payload = {
      idEntrepostoAduaneiro: item.value.idEntrepostoAduaneiro,
      idEmbarquePackingListProduto: plp.idEmbarquePackingListProduto,
      idEmbarque: plp.idEmbarque
    }
    await api.post('/EntrepostoAduaneiroItems', payload)
    emit('show-notification', { message: 'PLP adicionado ao regime com sucesso.', type: 'success' })
    await carregarItensRegime()
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao adicionar PLP ao regime.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingItem.value = false
  }
}

// ============================================================
// REMOVER PLP (DELETE via endpoint dedicado)
// ============================================================
const itemParaRemover = ref(null)
const confirmModal = ref(null)

const removerItem = (item) => {
  itemParaRemover.value = item
  confirmModal.value?.show()
}

const confirmarRemocaoItem = async (confirmado) => {
  if (!confirmado || !itemParaRemover.value) {
    itemParaRemover.value = null
    return
  }
  const itemRemover = itemParaRemover.value
  itemParaRemover.value = null

  try {
    await api.delete(`/EntrepostoAduaneiroItems/${itemRemover.idEntrepostoAduaneiroItem}`)
    emit('show-notification', { message: 'PLP removido do regime com sucesso.', type: 'success' })
    await carregarItensRegime()
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao remover PLP do regime.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const formatarNumeroView = (valor, decimais = 4) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

// ============================================================
// CICLO DE VIDA
// ============================================================
onMounted(async () => {
  await carregarListas()
  if (isEditing.value && item.value.lstStatusRegime) {
    statusOriginal.value = item.value.lstStatusRegime
    isRegimeSaved.value = true
    await carregarItensRegime()
  }
})
</script>

<template>
  <div class="entreposto-cadastro-wrapper">
    <FormularioCadastro
      :module-name="'ENTREPOSTO'"
      :is-loading="isLoading || isLoadingListas"
      :is-editing="isEditing"
      :entity-info="entityInfoCalculado"
      :desabilitar-salvar="isReadOnly"
      :ocultar-salvar="!isRegimeSaved"
      :accordion-state="accordionState"
      @submit="salvar"
      @cancelar="handleCancel"
    >
      <template #acoes-header v-if="isEditing">
        <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
        <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
        <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
      </div>

      <!-- FRAME 1: IDENTIFICAÇÃO -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.identificacao = !accordionState.identificacao">
          <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i> Identificação do Regime</h5>
          <div>
            <span v-if="regimeSalvo" class="badge bg-success text-white me-2 fs-6"><i class="bi bi-check-lg"></i> Regime #{{ item.idEntrepostoAduaneiro }}</span>
            <i :class="['bi', accordionState.identificacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
        </div>
        <div class="card-body" v-show="accordionState.identificacao">
          <div class="row g-3">
            <div class="col-md-4">
              <CampoBusca
                id="idFilial"
                label="Filial"
                v-model="item.idFilial"
                :configuracao-busca="configBuscaFilial"
                required
                :disabled="isReadOnly || camposIdentificacaoLocked"
                @show-notification="p => emit('show-notification', p)"
              />
            </div>

            <div class="col-md-4">
              <CampoInput
                id="numeroDocumentoAdmissao"
                label="Nº Documento de Admissão"
                v-model="item.numeroDocumentoAdmissao"
                required
                :disabled="isReadOnly || camposIdentificacaoLocked"
                descricao-informativa="DI ou DUIMP de admissão no regime."
              />
            </div>

            <div class="col-md-4">
              <CampoInput
                id="dataRegistroAdmissao"
                label="Data de Registro"
                type="date"
                v-model="item.dataRegistroAdmissao"
                required
                :disabled="isReadOnly || camposIdentificacaoLocked"
              />
            </div>

            <div class="col-md-4">
              <CampoBusca
                id="idLocalEstoque"
                label="Local de Estoque"
                v-model="item.idLocalEstoque"
                :configuracao-busca="configBuscaLocalEstoque"
                required
                :disabled="isReadOnly || camposIdentificacaoLocked"
                @show-notification="p => emit('show-notification', p)"
              />
            </div>

            <div class="col-md-4">
              <CampoSelect
                id="lstStatusRegime"
                label="Status"
                v-model="item.lstStatusRegime"
                :options="listas.LstStatusRegime || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                required
                :disabled="isReadOnly"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Botão Salvar Regime e Liberar Itens (apenas criação) -->
      <div class="mb-4" v-if="!isRegimeSaved">
        <button
          type="button"
          class="btn btn-success px-4"
          @click="salvarRegimeLocalmente"
          :disabled="isSavingRegimeLocal || !item.numeroDocumentoAdmissao || !item.dataRegistroAdmissao || !item.idLocalEstoque || !item.dataConcessao || !item.dataVencimentoOriginal || !item.idFilial"
        >
          <span v-if="isSavingRegimeLocal" class="spinner-border spinner-border-sm me-2"></span>
          <i v-else class="bi bi-save me-2"></i>
          Salvar Regime e Liberar Itens
        </button>
      </div>

      <!-- FRAME 2: TEMPORALIDADE -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.temporalidade = !accordionState.temporalidade">
          <h5 class="mb-0"><i class="bi bi-calendar-check me-2 text-primary"></i> Temporalidade</h5>
          <i :class="['bi', accordionState.temporalidade ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
        <div class="card-body" v-show="accordionState.temporalidade">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoInput
                id="dataConcessao"
                label="Data de Concessão"
                type="date"
                v-model="item.dataConcessao"
                required
                :disabled="isReadOnly || camposTemporalidadeLocked"
                descricao-informativa="Data em que a RFB liberou a entrada no regime."
              />
            </div>

            <div class="col-md-3">
              <CampoInput
                id="dataVencimentoOriginal"
                label="Vencimento Original"
                type="date"
                v-model="item.dataVencimentoOriginal"
                required
                :disabled="isReadOnly || camposTemporalidadeLocked"
                descricao-informativa="1 ano após a concessão. Calculado automaticamente."
              />
            </div>

            <div class="col-md-3 d-flex align-items-center pt-4">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="isRegimeProrrogado"
                  v-model="item.isRegimeProrrogado"
                  :disabled="isReadOnly || camposTemporalidadeLocked || item.lstStatusRegime !== ENTREPOSTO_ST_REGIME.Ativo"
                />
                <label class="form-check-label" for="isRegimeProrrogado">
                  Regime Prorrogado
                </label>
              </div>
            </div>

            <div class="col-md-3" v-if="item.isRegimeProrrogado">
              <CampoInput
                id="dataVencimentoProrrogada"
                label="Vencimento Prorrogado"
                type="date"
                v-model="item.dataVencimentoProrrogada"
                :disabled="isReadOnly || camposTemporalidadeLocked"
                descricao-informativa="Mais 1 ano adicional."
              />
            </div>
          </div>
        </div>
      </div>

      <!-- TOGGLE DE MODO DE TRANSFERÊNCIA (antes do Frame de Itens) -->
      <div class="card mb-4 border-info" v-if="!isRegimeSaved">
        <div class="card-body">
          <div class="row g-3 align-items-center">
            <div class="col-md-7">
              <div class="d-flex align-items-center gap-3">
                <div class="form-check form-switch mb-0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="gerarTransferenciaEstoque"
                    :checked="gerarTransferencia"
                    @change="gerarTransferencia = $event.target.checked"
                    :disabled="isReadOnly"
                    style="transform: scale(1.2);"
                  />
                  <label class="form-check-label fw-semibold ms-1" for="gerarTransferenciaEstoque">
                    ☑ Gerar transferência de estoque <span class="text-muted fw-normal">(Modo A — c/ MOV)</span>
                  </label>
                </div>
              </div>
              <div class="mt-1 ms-1" style="margin-left: 3.5rem;">
                <label class="text-muted small" style="cursor: pointer;" @click="gerarTransferencia = !gerarTransferencia">
                  <input
                    type="radio"
                    :checked="!gerarTransferencia"
                    class="me-1"
                    style="cursor: pointer;"
                  />
                  Apenas vincular material no recinto <span class="text-muted">(Modo B — s/ MOV)</span>
                </label>
              </div>
            </div>
            <div class="col-md-5">
              <div class="text-muted small p-2 rounded" style="background: var(--bs-info-bg-subtle);">
                <i class="bi bi-info-circle me-1"></i>
                {{ modoLabel }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FRAME 3: ITENS DO REGIME (SMARTMODE) -->
      <fieldset :disabled="isItensFrameBlocked" class="mb-4">
        <div class="card border-primary" :class="{ 'frame-bloqueado': isItensFrameBlocked }">
          <div class="card-header bg-primary text-white cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.itens = !accordionState.itens">
            <h5 class="mb-0 text-white"><i class="bi bi-list-check me-2"></i> Itens do Regime</h5>
            <div class="d-flex align-items-center gap-3">
              <span class="badge bg-light text-primary fs-6">{{ itensRegime.length }} {{ itensRegime.length === 1 ? 'item' : 'itens' }}</span>
              <button type="button" class="btn btn-sm btn-light py-0" @click.stop="togglePainelPLPs" v-if="!isReadOnly && isRegimeSaved" :title="mostrarPainelPLPs ? 'Fechar painel' : 'Adicionar Itens'">
                <i class="bi" :class="mostrarPainelPLPs ? 'bi-x-lg' : 'bi-plus-lg'"></i>
              </button>
              <i :class="['bi', accordionState.itens ? 'bi-chevron-up text-white' : 'bi-chevron-down text-white']"></i>
            </div>
          </div>
          <div class="card-body border-top border-secondary-subtle p-0" v-show="accordionState.itens">
            <!-- Guardião: Regime não salvo -->
            <div v-if="!isRegimeSaved" class="text-center text-muted py-4">
              <i class="bi bi-info-circle me-2"></i>
              Salve o regime primeiro para gerenciar os PLPs.
            </div>

            <template v-else>
              <!-- SmartMode: Painel de PLPs Disponíveis -->
              <div v-if="mostrarPainelPLPs" class="smartmode-panel border-bottom p-3" style="background: var(--bs-warning-bg-subtle);">
                <div class="row g-2 mb-2">
                  <div class="col-md-4">
                    <input type="text" class="form-control form-control-sm" v-model="filtroEmbarque" placeholder="Filtrar por Embarque (ID)...">
                  </div>
                  <div class="col-md-4">
                    <input type="text" class="form-control form-control-sm" v-model="filtroProduto" placeholder="Filtrar por Produto (nome ou SKU)...">
                  </div>
                  <div class="col-md-2">
                    <button class="btn btn-sm btn-warning w-100" @click="carregarPLPsDisponiveis">
                      <i class="bi bi-search me-1"></i> Buscar
                    </button>
                  </div>
                  <div class="col-md-2">
                    <button class="btn btn-sm btn-outline-secondary w-100" @click="togglePainelPLPs">
                      <i class="bi bi-x-lg me-1"></i> Fechar
                    </button>
                  </div>
                </div>

                <div v-if="isLoadingPLPs" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-warning"></div>
                  <span class="ms-2">Carregando PLPs disponíveis...</span>
                </div>

                <template v-else-if="embarquesAgrupados.length === 0">
                  <div class="text-center text-muted py-3">
                    <i class="bi bi-inbox fs-3 d-block mb-1"></i>
                    <small>Nenhum PLP disponível. Verifique se existem embarques Desembaraçados com saldo disponível.</small>
                  </div>
                </template>

                <template v-else>
                  <div v-for="grupo in embarquesAgrupados" :key="grupo.idEmbarque" class="mb-2">
                    <div class="d-flex align-items-center gap-2 p-2 rounded cursor-pointer" style="background: var(--bs-warning-bg-subtle); border: 1px solid var(--bs-warning-border-subtle);" @click="toggleEmbarqueExpandido(grupo.idEmbarque)">
                      <i class="bi" :class="embarqueExpandido === grupo.idEmbarque ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                      <i class="bi bi-ship"></i>
                      <strong>{{ grupo.numeroEmbarque || `Embarque #${grupo.idEmbarque}` }}</strong>
                      <span class="badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle ms-2">{{ grupo.plps.length }} PLPs</span>
                    </div>

                    <div v-if="embarqueExpandido === grupo.idEmbarque" class="row g-2 mt-1 ps-4">
                      <div v-for="plp in grupo.plps" :key="plp.idEmbarquePackingListProduto" class="col-md-4">
                        <div class="card border-warning border-opacity-25 h-100">
                          <div class="card-body p-2">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <div class="fw-bold small" v-if="plp.sku"><code>{{ plp.sku }}</code></div>
                                <div class="small">{{ plp.nomeProduto || 'Produto #' + plp.idProduto }}</div>
                                <div class="text-muted" style="font-size: 0.7rem;">
                                  <span v-if="plp.nomeFornecedor">{{ plp.nomeFornecedor }}</span>
                                  <span v-if="plp.numeroDocumentoEntrada" class="ms-1">| Doc: {{ plp.numeroDocumentoEntrada }}</span>
                                </div>
                                <div class="mt-1" style="font-size: 0.7rem;">
                                  <span class="fw-bold">Qtd: {{ formatarNumeroView(plp.quantidadeDisponivelUnBasica) }} {{ plp.lstUnidadeMedidaBasica }}</span>
                                </div>
                              </div>
                              <button type="button" class="btn btn-sm btn-success text-white py-0 px-2" @click.stop="adicionarPLP(plp)" :disabled="isSavingItem" title="Adicionar PLP ao regime">
                                <i class="bi bi-plus-lg"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Paginação dos PLPs Disponíveis -->
                <div v-if="paginacao.TotalCount > 0" class="d-flex flex-wrap align-items-center justify-content-between w-100 gap-2 mt-2">
                  <span class="text-muted small">
                    Página <strong>{{ paginacao.CurrentPage || 0 }}</strong> de <strong>{{ paginacao.TotalPages || 0 }}</strong>
                    <span class="d-none d-sm-inline">(Total: {{ paginacao.TotalCount || 0 }} registos)</span>
                  </span>
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary" @click="mudarPaginaPLPs(paginacao.CurrentPage - 1)" :disabled="paginacao.CurrentPage <= 1">
                      <i class="bi bi-chevron-left"></i> Anterior
                    </button>
                    <button class="btn btn-outline-secondary ms-2" @click="mudarPaginaPLPs(paginacao.CurrentPage + 1)" :disabled="paginacao.CurrentPage >= paginacao.TotalPages">
                      Próxima <i class="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Tabela de Itens Alocados -->
              <div v-if="isLoadingItens" class="text-center text-muted py-4">
                <span class="spinner-border spinner-border-sm me-2"></span>
                A carregar itens...
              </div>
              <div v-else class="admin-table-container border-0 shadow-none mb-0">
                <table class="table admin-table m-0">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Produto</th>
                      <th class="text-end">Qtd</th>
                      <th>Unidade</th>
                      <th class="text-end" v-if="!isReadOnly">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="linha in itensRegime" :key="linha.idEntrepostoAduaneiroItem">
                      <td><code>{{ linha.sku || '-' }}</code></td>
                      <td>{{ linha.nomeProduto || `PLP #${linha.idEmbarquePackingListProduto}` }}</td>
                      <td class="text-end fw-bold">{{ formatarNumeroView(linha.quantidadeDisponivel) }}</td>
                      <td>{{ linha.lstUnidadeMedidaBasica || '-' }}</td>
                      <td class="text-end" v-if="!isReadOnly">
                        <button type="button" class="btn-icon text-danger" @click="removerItem(linha)" title="Remover do regime"><i class="bi bi-trash-fill"></i></button>
                      </td>
                    </tr>
                    <tr v-if="itensRegime.length === 0">
                      <td colspan="5" class="text-center text-muted py-4">
                        Nenhum PLP vinculado a este regime. Clique em <i class="bi bi-plus-lg"></i> para adicionar.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </fieldset>
    </FormularioCadastro>

    <!-- CONFIRMAÇÃO DE EXCLUSÃO DE ITEM -->
    <ConfirmationModal
      ref="confirmModal"
      title="Remover PLP do Regime"
      message="Deseja realmente remover este PLP do regime de entreposto?"
      confirm-button-text="Sim, Remover"
      cancel-button-text="Não, Manter"
      @confirmed="confirmarRemocaoItem"
    />

    <!-- AUDITORIA -->
    <AuditLogViewer
      v-if="mostrarAuditoria"
      tabela="EntrepostoAduaneiro"
      :chave-primaria="item.idEntrepostoAduaneiro"
      :titulo-registro="`Regime ${item.idEntrepostoAduaneiro}`"
      contexto="tenant"
      :tabelas-filhas="['EntrepostoAduaneiroItem']"
      chave-estrangeira="IdEntrepostoAduaneiro"
      @fechar="mostrarAuditoria = false"
    />
  </div>
</template>

<style scoped>
.entreposto-cadastro-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

.frame-bloqueado {
  opacity: 0.7;
  background-color: var(--bs-secondary-bg);
  pointer-events: none;
  border-radius: 0.375rem;
}
</style>
